import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import Seo from './Seo';
import { colors, breakpoints } from '../../styles/variables';

const Page = styled.div`
  background-color: ${colors.lightGray};
  min-height: 100vh;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${(p) => p.$accent} 140%);
  color: ${colors.white};
  /* 64px(고정 헤더 높이) + 56px(시각적 상단 여백) / 56px(하단 여백) */
  padding: 120px 24px 56px;
`;

const HeroInner = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 12px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: 1.7;
  max-width: 760px;
`;

const Body = styled.section`
  max-width: 920px;
  margin: 0 auto;
  padding: 28px 24px 80px;
`;

const Card = styled.article`
  background: ${colors.white};
  border-radius: 12px;
  padding: 40px 44px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

  @media (max-width: ${breakpoints.mobile}) {
    padding: 28px 24px;
  }

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: ${colors.primary};
    margin: 32px 0 12px;
  }

  h2:first-child {
    margin-top: 0;
  }

  h3 {
    font-size: 16px;
    font-weight: 700;
    color: ${colors.primary};
    margin: 20px 0 8px;
  }

  p {
    font-size: 15px;
    line-height: 1.8;
    color: ${colors.textGray};
    margin: 0 0 12px;
    white-space: pre-line;
  }

  ul,
  ol {
    margin: 0 0 16px 20px;
    padding-left: 4px;

    li {
      font-size: 15px;
      line-height: 1.8;
      color: ${colors.textGray};
      list-style: disc;
    }
  }

  ol li {
    list-style: decimal;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 14px;

    th,
    td {
      border: 1px solid ${colors.lightGray};
      padding: 10px 12px;
      text-align: left;
      vertical-align: top;
    }

    th {
      background: ${colors.lightGray};
      font-weight: 600;
      color: ${colors.primary};
    }

    td {
      color: ${colors.textGray};
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    table {
      display: block;
      border: none;
      font-size: 14px;

      thead {
        display: none;
      }

      tbody,
      tr {
        display: block;
      }

      tr {
        border: 1px solid ${colors.lightGray};
        border-radius: 10px;
        margin-bottom: 12px;
        padding: 4px 0;
        background: ${colors.white};
      }

      td {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        border: none;
        border-bottom: 1px solid ${colors.lightGray};
        padding: 10px 14px;
        line-height: 1.6;
      }

      tr td:last-child {
        border-bottom: none;
      }

      td::before {
        content: attr(data-label);
        flex: 0 0 35%;
        max-width: 35%;
        font-weight: 600;
        color: ${colors.primary};
        word-break: keep-all;
      }
    }
  }
`;

const LegalLayout = ({
  title,
  subtitle,
  helmetTitle,
  helmetDescription,
  accentColor,
  jsonLd,
  children,
}) => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <Seo
        title={helmetTitle || title}
        description={helmetDescription || subtitle || `${title} - ElectroWave Club`}
        path={location.pathname}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Hero $accent={accentColor || colors.secondary}>
        <HeroInner>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeroInner>
      </Hero>
      <Body>
        <Card>{children}</Card>
      </Body>
    </Page>
  );
};

export default LegalLayout;
